import { datePl, type MeDto, type PackageStatus, type Readiness, type WarehouseDashboardDto } from '@bqcrm/shared';
import { Anchor, Badge, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { PackageStatusBadge, ReadinessBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { SectionCard } from '../../components/ui';
import { plural } from '../../format';
import { WarehouseSelect } from './common';

/** Kafelek z następnym działaniem: liczba, krótka lista i link do pełnego widoku. */
function Tile({ title, count, tone, to, toLabel, children }: { title: string; count: number; tone?: 'red' | 'orange' | 'blue'; to: string; toLabel: string; children?: ReactNode }) {
  return (
    <SectionCard
      title={
        <Group gap={8}>
          <Text fw={600}>{title}</Text>
          <Badge variant={count ? 'filled' : 'light'} color={count ? (tone ?? 'blue') : 'gray'}>
            {count}
          </Badge>
        </Group>
      }
      padding="sm"
      actions={
        <Anchor component={Link} to={to} size="sm">
          {toLabel}
        </Anchor>
      }
    >
      {count === 0 ? (
        <Text size="sm" c="dimmed">
          Nic do zrobienia.
        </Text>
      ) : (
        <Stack gap={4}>{children}</Stack>
      )}
    </SectionCard>
  );
}

const Row = ({ to, children }: { to: string; children: ReactNode }) => (
  <Anchor component={Link} to={to} size="sm" c="var(--mantine-color-text)" underline="hover">
    {children}
  </Anchor>
);

/** Pulpit magazyniera (docs/14 §3): kafelki z następnym działaniem; wybór magazynu u góry. */
export function WarehouseDashboardPage({ me }: { me: MeDto }) {
  const [params, setParams] = useSearchParams();
  const warehouseId = params.get('magazyn');
  const q = useQuery({
    queryKey: ['warehouse-dashboard', warehouseId],
    queryFn: () => api<WarehouseDashboardDto>(`/warehouse-dashboard${warehouseId ? `?warehouseId=${warehouseId}` : ''}`),
    refetchInterval: 120_000,
  });
  const d = q.data;
  const who = (x: { caseNumber: string | null; taskNumber: string | null }) => [x.caseNumber, x.taskNumber].filter(Boolean).join(' · ') || '—';
  return (
    <>
      <PageHeader
        title="Pulpit magazynu"
        description="Co trzeba zrobić teraz: zgłoszenia ekip, pakiety na dziś i jutro, braki zagrażające realizacjom, dostawy i faktury do przyjęcia."
        actions={
          <WarehouseSelect
            w={260}
            placeholder="Wszystkie magazyny"
            clearable
            allowDeselect
            value={warehouseId}
            onChange={(v) => {
              const n = new URLSearchParams(params);
              if (v) n.set('magazyn', v);
              else n.delete('magazyn');
              setParams(n, { replace: true });
            }}
            aria-label="Magazyn"
          />
        }
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        {d && (
          <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} spacing="md">
            <Tile title="Braki zagrażające realizacjom" count={d.atRisk.length} tone="red" to="/magazyn/zapotrzebowania?widok=braki" toLabel="Braki">
              {d.atRisk.slice(0, 8).map((x) => (
                <Group key={x.demandId} justify="space-between" wrap="nowrap">
                  <Row to={`/magazyn/zapotrzebowania?zapotrzebowanie=${x.demandId}`}>
                    {who(x)} {x.neededBy ? `· na ${datePl(x.neededBy)}` : ''}
                  </Row>
                  <ReadinessBadge readiness={x.readiness as Readiness} size="xs" />
                </Group>
              ))}
            </Tile>
            <Tile title="Zgłoszenia ekip do przejrzenia" count={d.unreviewed.length + d.unmatched} tone="orange" to="/magazyn/zapotrzebowania?widok=zmienione" toLabel="Zapotrzebowania">
              {d.unreviewed.slice(0, 8).map((x) => (
                <Group key={x.id} justify="space-between" wrap="nowrap">
                  <Row to={`/magazyn/zapotrzebowania?zapotrzebowanie=${x.id}`}>{who(x)}</Row>
                  <Badge size="xs" variant="light" color={x.changed ? 'orange' : 'cyan'}>
                    {x.changed ? 'zmienione' : 'nowe'}
                  </Badge>
                </Group>
              ))}
              {d.unmatched > 0 && (
                <Row to="/magazyn/zapotrzebowania?widok=do_dopasowania">
                  {d.unmatched} {plural(d.unmatched, 'pozycja opisowa', 'pozycje opisowe', 'pozycji opisowych')} do dopasowania
                </Row>
              )}
            </Tile>
            <Tile title="Pakiety na dziś" count={d.packagesToday.length} tone="orange" to="/magazyn/pakiety" toLabel="Pakiety">
              {d.packagesToday.slice(0, 8).map((p) => (
                <Group key={p.id} justify="space-between" wrap="nowrap">
                  <Row to={`/magazyn/pakiety?pakiet=${p.id}`}>
                    {p.number} · {p.teamName ?? who(p)}
                  </Row>
                  <PackageStatusBadge status={p.status as PackageStatus} />
                </Group>
              ))}
            </Tile>
            <Tile title="Pakiety na jutro" count={d.packagesTomorrow.length} to="/magazyn/pakiety" toLabel="Pakiety">
              {d.packagesTomorrow.slice(0, 8).map((p) => (
                <Group key={p.id} justify="space-between" wrap="nowrap">
                  <Row to={`/magazyn/pakiety?pakiet=${p.id}`}>
                    {p.number} · {p.teamName ?? who(p)}
                  </Row>
                  <PackageStatusBadge status={p.status as PackageStatus} />
                </Group>
              ))}
            </Tile>
            <Tile title="Dostawy" count={d.lateDeliveries.length + d.deliveriesToday.length} tone={d.lateDeliveries.length ? 'red' : 'blue'} to="/magazyn/zamowienia" toLabel="Zamówienia">
              {d.lateDeliveries.map((o) => (
                <Row key={o.id} to={`/magazyn/zamowienia?zamowienie=${o.id}`}>
                  <Text span c="red" fw={600} size="sm">
                    po terminie
                  </Text>{' '}
                  {o.number} · {o.supplierName} {o.expectedDate ? `(${datePl(o.expectedDate)})` : ''}
                </Row>
              ))}
              {d.deliveriesToday.map((o) => (
                <Row key={o.id} to={`/magazyn/zamowienia?zamowienie=${o.id}`}>
                  dziś: {o.number} · {o.supplierName}
                </Row>
              ))}
            </Tile>
            {d.invoicesToReceive && (
              <Tile title="Faktury KSeF do przyjęcia" count={d.invoicesToReceive.length} to="/finanse?widok=zakupy" toLabel="Zakupy">
                {d.invoicesToReceive.slice(0, 8).map((f) => (
                  <Row key={f.id} to="/finanse?widok=zakupy">
                    {f.invoiceNumber} · {f.sellerName} {f.issueDate ? `(${datePl(f.issueDate)})` : ''}
                  </Row>
                ))}
              </Tile>
            )}
            <Tile title="Do zamówienia" count={d.suggestions} tone="orange" to="/magazyn/zamowienia?widok=podpowiedzi" toLabel="Podpowiedzi zakupowe">
              <Text size="sm">
                {d.suggestions} {plural(d.suggestions, 'produkt', 'produkty', 'produktów')} do zamówienia (braki realizacji i minimum).
              </Text>
            </Tile>
            <Tile title="Poniżej minimum" count={d.belowMin} to="/magazyn/stany?filtr=ponizej_minimum" toLabel="Stany">
              <Text size="sm">Stan dostępny poniżej ustawionego minimum.</Text>
            </Tile>
          </SimpleGrid>
        )}
      </QueryState>
      {!can(me, 'zapotrzebowania.podglad') && (
        <Text size="xs" c="dimmed" mt="md">
          Braki i zgłoszenia ekip widzą osoby z podglądem zapotrzebowań.
        </Text>
      )}
    </>
  );
}
